import React, { useState, useEffect } from 'react';
import { supabase } from '../lib/supabase';
import { useAuth } from '../hooks/useAuth';
import { useOrganization } from '../hooks/useOrganization';
import { Plus, Search, Edit2, Trash2, Share2, Settings, Upload, Calendar, ChevronLeft, ChevronRight, Check, X, GripVertical } from 'lucide-react';
import { Link } from 'react-router-dom';
import type { Event } from '../types';
import EventSettings from './EventSettings';
import ShareModal from './ShareModal';
import LiquidGlass from './LiquidGlass';

export default function Events() {
  const { user } = useAuth();
  const { organization } = useOrganization();
  const [events, setEvents] = useState<Event[]>([]);
  const [filteredEvents, setFilteredEvents] = useState<Event[]>([]);
  const [eventResultsStatus, setEventResultsStatus] = useState<Record<string, boolean>>({});
  const [loadingEventResultsStatus, setLoadingEventResultsStatus] = useState(true);
  const [loading, setLoading] = useState(true);
  const [showNewEvent, setShowNewEvent] = useState(false);
  const [newEvent, setNewEvent] = useState({ name: '', date: '' });
  const [error, setError] = useState<string | null>(null);
  const [editingEventId, setEditingEventId] = useState<string | null>(null);
  const [editingName, setEditingName] = useState('');
  const [deletingEventId, setDeletingEventId] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [showSettings, setShowSettings] = useState<string | null>(null);
  const [showShareModal, setShowShareModal] = useState<string | null>(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [initialLoadComplete, setInitialLoadComplete] = useState(false);
  const [loadingEvents, setLoadingEvents] = useState(false);
  
  // Show 4 recent events as cards on mobile/tablet, 6 on desktop
  const RECENT_EVENTS_COUNT_MOBILE = 4;
  const RECENT_EVENTS_COUNT_DESKTOP = 6;
  const LIST_EVENTS_PER_PAGE = 10;
  const [recentEventsCount, setRecentEventsCount] = useState(RECENT_EVENTS_COUNT_MOBILE);

  useEffect(() => {
    // Update recent events count based on screen size
    const handleResize = () => {
      if (window.innerWidth >= 1024) { // lg breakpoint
        setRecentEventsCount(RECENT_EVENTS_COUNT_DESKTOP);
      } else {
        setRecentEventsCount(RECENT_EVENTS_COUNT_MOBILE);
      }
    };

    // Set initial value
    handleResize();

    // Add event listener
    window.addEventListener('resize', handleResize);

    // Clean up
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    if (user) {
      setLoadingEvents(true);
      loadEvents();
    }
  }, [user, organization]);

  useEffect(() => {
    filterEvents();
  }, [searchTerm, events]);

  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm]);

  const filterEvents = () => {
    if (!searchTerm.trim()) {
      setFilteredEvents(events);
      return;
    }

    const term = searchTerm.toLowerCase().trim();
    const filtered = events.filter(event => 
      event.name.toLowerCase().includes(term) || 
      formatDate(event.date).includes(term)
    );
    setFilteredEvents(filtered);
  };

  const loadEvents = async () => {
    if (!user || !organization) return;
    
    try {
      // First, clear any existing events to prevent flashing
      setEvents([]);
      setFilteredEvents([]);
      setEventResultsStatus({});
      
      // Create query to get all relevant events
      let query = supabase
        .from('events')
        .select('*');

      // If user belongs to an organization, fetch both personal and organization events
      if (organization?.id) {
        query = query.or(`user_id.eq.${user.id},organization_id.eq.${organization.id}`);
      } else {
        // Otherwise, just fetch personal events
        query = query.eq('user_id', user.id);
      }

      const { data, error } = await query.order('date', { ascending: false });

      if (error) throw error;
      
      const eventsData = data || [];
      setEvents(eventsData);
      setFilteredEvents(eventsData);
      
      // Load results status for each event
      if (eventsData.length > 0) {
        // Set events immediately so they can be displayed
        setEvents(eventsData);
        setFilteredEvents(eventsData);
        setLoadingEvents(false);
        
        // Then load results status in the background
        setLoadingEventResultsStatus(true);
        loadEventResultsStatus(eventsData.map(e => e.id));
      } else {
        setEvents([]);
        setFilteredEvents([]);
        setLoadingEvents(false);
      }
    } catch (err: any) {
      console.error('Error loading events:', err);
      setError(err.message);
      setLoadingEvents(false);
    } finally {
      // Always set loading to false and initialLoadComplete to true
      setLoading(false);
      setInitialLoadComplete(true);
    }
  };

  const loadEventResultsStatus = async (eventIds: string[]) => {
    try {
      // Create a new status map to avoid state mutation issues
      let statusMap: Record<string, boolean> = {};
      
      // Check which events have results by counting results for each event
      // Initialize all events as having no results
      eventIds.forEach(id => {
        statusMap[id] = false;
      });

      // Check each event individually for results
      for (const eventId of eventIds) {
        const { count, error } = await supabase
          .from('results')
          .select('*', { count: 'exact', head: true })
          .eq('event_id', eventId);

        if (error) throw error;

        // Update status if event has results
        if (count && count > 0) {
          statusMap[eventId] = true;
        }
      }

      setEventResultsStatus(statusMap);
      setLoadingEventResultsStatus(false);
    } catch (err) {
      console.error('Error loading event results status:', err);
      setLoadingEventResultsStatus(false);
    } finally {
      // Nothing needed here as we already set loading state in the try and catch blocks
    }
  };

  const handleCreateEvent = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    
    try {
      const eventData = {
        ...newEvent,
        user_id: user?.id,
        organization_id: organization?.id || null
      };

      const { data, error } = await supabase
        .from('events')
        .insert([eventData])
        .select()
        .single();

      if (error) throw error;
      
      setEvents(prev => [data, ...prev]);
      // New event has no results
      setEventResultsStatus(prev => ({ ...prev, [data.id]: false }));
      setNewEvent({ name: '', date: '' });
      setShowNewEvent(false);
    } catch (err: any) {
      setError(err.message);
    }
  };

  const handleUpdateName = async (eventId: string) => {
    try {
      setError(null);
      
      const { error } = await supabase
        .from('events')
        .update({ name: editingName })
        .eq('id', eventId);

      if (error) throw error;
      
      setEvents(prev => prev.map(event => 
        event.id === eventId ? { ...event, name: editingName } : event
      ));
      setEditingEventId(null);
    } catch (err: any) {
      setError(err.message);
      setEditingEventId(null);
    }
  };

  const handleDeleteEvent = async (eventId: string) => {
    try {
      const { error } = await supabase
        .from('events')
        .delete()
        .eq('id', eventId);

      if (error) throw error;
      
      setEvents(prev => prev.filter(event => event.id !== eventId));
      setEventResultsStatus(prev => {
        const newStatus = { ...prev };
        delete newStatus[eventId];
        return newStatus;
      });
      setDeletingEventId(null);
    } catch (err: any) {
      setError(err.message);
    }
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-GB', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric'
    });
  };

  // Check if event has results
  const hasResults = (eventId: string) => {
    return eventResultsStatus[eventId] === true;
  };

  // Split events into recent (cards) and others (list)
  const recentEvents = filteredEvents.slice(0, recentEventsCount);
  const otherEvents = filteredEvents.slice(recentEventsCount);
  
  // Pagination for other events
  const totalPages = Math.ceil(otherEvents.length / LIST_EVENTS_PER_PAGE);
  const startIndex = (currentPage - 1) * LIST_EVENTS_PER_PAGE;
  const endIndex = startIndex + LIST_EVENTS_PER_PAGE;
  const currentOtherEvents = otherEvents.slice(startIndex, endIndex);

  const goToPage = (page: number) => {
    setCurrentPage(Math.max(1, Math.min(page, totalPages)));
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-gray-900"></div>
      </div>
    );
  }

  return (
    <div>
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-6">
        <h1 className="text-3xl font-bold text-gray-800">Events</h1>
        <LiquidGlass variant="black-button" hover glowColor="black" bgOpacity="high">
          <button
            onClick={() => setShowNewEvent(true)}
            className="flex items-center gap-2 px-6 py-2 text-white font-medium transition-all duration-200 shadow-lg"
          >
            <Plus className="w-4 h-4" />
            New Event
          </button>
        </LiquidGlass>
      </div>

      <div className="mb-6">
        <div className="relative max-w-md">
          <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
            <Search className="h-5 w-5 text-gray-400" />
          </div>
          <input
            type="text"
            placeholder="Search events..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="block w-full pl-10 pr-3 py-2 bg-white/60 border border-gray-200 rounded-lg text-gray-800 placeholder-gray-500 focus:border-blue-300 focus:ring-2 focus:ring-blue-200 transition-all duration-200 shadow-lg"
          />
        </div>
      </div>

      {error && (
        <LiquidGlass className="bg-red-100/60 border-red-200/60 p-4 mb-4">
          <p className="text-red-700">{error}</p>
        </LiquidGlass>
      )}

      {showNewEvent && (
        <LiquidGlass variant="card" className="mb-8 p-6">
          <form onSubmit={handleCreateEvent} className="space-y-4">
            <div>
              <label htmlFor="name" className="block text-sm font-medium text-gray-700">
                Event Name
              </label>
              <input
                id="name"
                type="text"
                value={newEvent.name}
                onChange={(e) => setNewEvent({ ...newEvent, name: e.target.value })}
                className="mt-1 block w-full rounded-lg bg-white/60 border border-gray-200 text-gray-800 placeholder-gray-500 focus:border-blue-300 focus:ring-2 focus:ring-blue-200 px-3 py-2"
                required
              />
            </div>

            <div>
              <label htmlFor="date" className="block text-sm font-medium text-gray-700">
                Event Date
              </label>
              <input
                id="date"
                type="date"
                value={newEvent.date}
                onChange={(e) => setNewEvent({ ...newEvent, date: e.target.value })}
                className="mt-1 block w-full rounded-lg bg-white/60 border border-gray-200 text-gray-800 focus:border-blue-300 focus:ring-2 focus:ring-blue-200 px-3 py-2"
                required
              />
            </div>

            <div className="flex gap-2">
              <LiquidGlass variant="black-button" hover glowColor="black" bgOpacity="high">
                <button
                  type="submit"
                  className="px-4 py-2 text-white font-medium"
                >
                  Create Event
                </button>
              </LiquidGlass>
              <LiquidGlass variant="button" hover>
                <button
                  type="button"
                  onClick={() => setShowNewEvent(false)}
                  className="px-4 py-2 text-gray-600 hover:text-gray-800"
                >
                  Cancel
                </button>
              </LiquidGlass>
            </div>
          </form>
        </LiquidGlass>
      )}

      {/* Recent Events - Card Layout */}
      {recentEvents.length > 0 && (
        <div className="mb-8">
          <h2 className="text-xl font-semibold text-gray-800 mb-4">Recent Events</h2>
          <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
            {recentEvents.map((event) => (
              <LiquidGlass key={event.id} variant="card" className="p-6 h-auto flex flex-col" hover>
                <div className="flex justify-between items-start mb-2 flex-shrink-0">
                  {editingEventId === event.id ? (
                    <div className="flex-1 flex items-center gap-2">
                      <input
                        type="text"
                        autoFocus
                        value={editingName}
                        onChange={(e) => setEditingName(e.target.value)}
                        className="flex-1 px-3 py-1.5 bg-white/60 border border-gray-200 rounded-md text-gray-800 focus:border-blue-300 focus:ring-2 focus:ring-blue-200"
                        onKeyDown={(e) => {
                          if (e.key === 'Enter') {
                            handleUpdateName(event.id);
                          } else if (e.key === 'Escape') {
                            setEditingEventId(null);
                          }
                        }}
                      />
                      <button
                        onClick={() => handleUpdateName(event.id)}
                        className="text-green-600 hover:text-green-700 p-1.5 hover:bg-green-50 rounded-full transition-colors"
                        title="Save changes"
                      >
                        <Check className="w-5 h-5" />
                      </button>
                      <button
                        onClick={() => setEditingEventId(null)}
                        className="text-gray-500 hover:text-gray-700 p-1.5 hover:bg-gray-50 rounded-full transition-colors"
                        title="Cancel"
                      >
                        <X className="w-5 h-5" />
                      </button>
                    </div>
                  ) : (
                    <>
                      <h3 className="text-lg font-semibold text-gray-800 line-clamp-2 flex-1 pr-2">{event.name}</h3>
                      <div className="flex items-center gap-1 flex-shrink-0">
                        <button
                          onClick={() => setShowShareModal(event.id)}
                          className="text-gray-500 hover:text-gray-700 p-1 transition-colors duration-200"
                          title="Share results"
                        >
                          <Share2 className="w-5 h-5" />
                        </button>
                        <button
                          onClick={() => setShowSettings(event.id)}
                          className="text-gray-500 hover:text-gray-700 p-1 transition-colors duration-200"
                          title="Event settings"
                        >
                          <Settings className="w-5 h-5" />
                        </button>
                        <button
                          onClick={() => {
                            setEditingEventId(event.id);
                            setEditingName(event.name);
                          }}
                          className="text-gray-500 hover:text-gray-700 p-1 transition-colors duration-200"
                          title="Edit name"
                        >
                          <Edit2 className="w-5 h-5" />
                        </button>
                        <button
                          onClick={() => setDeletingEventId(event.id)}
                          className="text-gray-500 hover:text-red-600 p-1 transition-colors duration-200"
                          title="Delete event"
                        >
                          <Trash2 className="w-5 h-5" />
                        </button>
                      </div>
                    </>
                  )}
                </div>
                
                <div className="flex items-center justify-between mb-4 flex-shrink-0">
                  <p className="text-gray-600">
                    {formatDate(event.date)}
                  </p>
                  {loadingEventResultsStatus ? (
                    <div className="px-2 py-1 rounded-full text-xs font-medium bg-gray-100 flex items-center gap-1">
                      <div className="w-3 h-3 rounded-full border-2 border-gray-300 border-t-gray-600 animate-spin"></div>
                      <span>Loading...</span>
                    </div>
                  ) : (
                    <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                      hasResults(event.id) 
                        ? 'bg-green-100 text-green-800' 
                        : 'bg-gray-100 text-gray-600'
                    }`}>
                      {hasResults(event.id) ? 'Results Live' : 'No Results'}
                    </span>
                  )}
                </div>

                <div className="flex-1 flex flex-col justify-end">
                  <Link
                    to={`/upload/${event.id}`}
                    className="w-full"
                  >
                    <LiquidGlass variant="black-button" hover glowColor="black" bgOpacity="high">
                      <div className="flex items-center justify-center gap-2 px-4 py-2 text-white font-medium transition-colors duration-200">
                        <Upload className="w-4 h-4" />
                        {hasResults(event.id) ? 'Update Results' : 'Upload Results'}
                      </div>
                    </LiquidGlass>
                  </Link>
                </div>
              </LiquidGlass>
            ))}
          </div>
        </div>
      )}

      {/* Other Events - List Layout */}
      {otherEvents.length > 0 && (
        <div>
          <h2 className="text-xl font-semibold text-gray-800 mb-4">
            Other Events ({otherEvents.length})
          </h2>
          
          <LiquidGlass variant="card" className="overflow-hidden">
            <div className="divide-y divide-gray-200">
              {currentOtherEvents.map((event) => (
                <div key={event.id} className="p-4 hover:bg-gray-50/80 transition-colors duration-200">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-4 flex-1">
                      <Calendar className="w-5 h-5 text-gray-400" />
                      <div className="flex-1">
                        {editingEventId === event.id ? (
                          <div className="flex-1 flex items-center gap-2">
                            <input
                              type="text"
                              autoFocus
                              value={editingName}
                              onChange={(e) => setEditingName(e.target.value)}
                              className="flex-1 px-3 py-1.5 bg-white/60 border border-gray-200 rounded-md text-gray-800 focus:border-blue-300 focus:ring-2 focus:ring-blue-200"
                              onKeyDown={(e) => {
                                if (e.key === 'Enter') {
                                  handleUpdateName(event.id);
                                } else if (e.key === 'Escape') {
                                  setEditingEventId(null);
                                }
                              }}
                            />
                            <button
                              onClick={() => handleUpdateName(event.id)}
                              className="text-green-600 hover:text-green-700 p-1.5 hover:bg-green-50 rounded-full transition-colors"
                              title="Save changes"
                            >
                              <Check className="w-4 h-4" />
                            </button>
                            <button
                              onClick={() => setEditingEventId(null)}
                              className="text-gray-500 hover:text-gray-700 p-1.5 hover:bg-gray-50 rounded-full transition-colors"
                              title="Cancel"
                            >
                              <X className="w-4 h-4" />
                            </button>
                          </div>
                        ) : (
                          <>
                            <h3 className="font-medium text-gray-900">{event.name}</h3>
                            <div className="flex items-center gap-4 text-sm text-gray-500">
                              <span>{formatDate(event.date)}</span>
                              {loadingEventResultsStatus ? (
                                <div className="px-2 py-1 rounded-full text-xs font-medium bg-gray-100 flex items-center gap-1">
                                  <div className="w-3 h-3 rounded-full border-2 border-gray-300 border-t-gray-600 animate-spin"></div>
                                  <span>Loading...</span>
                                </div>
                              ) : (
                                <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                                  hasResults(event.id) 
                                    ? 'bg-green-100 text-green-800' 
                                    : 'bg-gray-100 text-gray-600'
                                }`}>
                                  {hasResults(event.id) ? 'Results Live' : 'No Results'}
                                </span>
                              )}
                            </div>
                          </>
                        )}
                      </div>
                    </div>
                    
                    <div className="flex items-center gap-2">
                      <Link to={`/upload/${event.id}`}>
                        <LiquidGlass variant="black-button" hover glowColor="black" bgOpacity="high">
                          <div className="flex items-center gap-2 px-3 py-2 text-white text-sm font-medium">
                            <Upload className="w-4 h-4" />
                            {hasResults(event.id) ? 'Update' : 'Upload'}
                          </div>
                        </LiquidGlass>
                      </Link>
                      
                      <button
                        onClick={() => setShowShareModal(event.id)}
                        className="text-gray-500 hover:text-gray-700 p-2 transition-colors duration-200"
                        title="Share results"
                      >
                        <Share2 className="w-4 h-4" />
                      </button>
                      <button
                        onClick={() => setShowSettings(event.id)}
                        className="text-gray-500 hover:text-gray-700 p-2 transition-colors duration-200"
                        title="Event settings"
                      >
                        <Settings className="w-4 h-4" />
                      </button>
                      <button
                        onClick={() => {
                          setEditingEventId(event.id);
                          setEditingName(event.name);
                        }}
                        className="text-gray-500 hover:text-gray-700 p-2 transition-colors duration-200"
                        title="Edit name"
                      >
                        <Edit2 className="w-4 h-4" />
                      </button>
                      <button
                        onClick={() => setDeletingEventId(event.id)}
                        className="text-gray-500 hover:text-red-600 p-2 transition-colors duration-200"
                        title="Delete event"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>

            {/* Pagination for Other Events */}
            {totalPages > 1 && (
              <div className="px-4 py-4 border-t border-gray-200/50 bg-gradient-to-r from-gray-50/80 via-white/60 to-gray-50/80">
                <div className="flex items-center justify-between">
                  <div className="flex flex-1 justify-between sm:hidden">
                    <LiquidGlass variant="button" hover>
                      <button
                        onClick={() => goToPage(currentPage - 1)}
                        disabled={currentPage === 1}
                        className="flex items-center gap-2 px-4 py-2 text-gray-600 hover:text-gray-800 disabled:opacity-50 font-medium"
                      >
                        <ChevronLeft className="w-4 h-4" />
                        Previous
                      </button>
                    </LiquidGlass>
                    <LiquidGlass variant="button" hover>
                      <button
                        onClick={() => goToPage(currentPage + 1)}
                        disabled={currentPage === totalPages}
                        className="flex items-center gap-2 px-4 py-2 text-gray-600 hover:text-gray-800 disabled:opacity-50 font-medium"
                      >
                        Next
                        <ChevronRight className="w-4 h-4" />
                      </button>
                    </LiquidGlass>
                  </div>
                  <div className="hidden sm:flex sm:flex-1 sm:items-center sm:justify-between">
                    <div>
                      <p className="text-sm text-gray-600 font-medium">
                        Showing <span className="font-medium">{startIndex + 1}</span> to{' '}
                        <span className="font-medium">
                          {Math.min(endIndex, otherEvents.length)}
                        </span>{' '}
                        of <span className="font-medium">{otherEvents.length}</span> events
                      </p>
                    </div>
                    <div>
                      <nav className="relative z-0 inline-flex gap-1" aria-label="Pagination">
                        <LiquidGlass variant="button" hover>
                          <button
                            onClick={() => goToPage(currentPage - 1)}
                            disabled={currentPage === 1}
                            className="relative inline-flex items-center px-3 py-2 text-gray-600 hover:text-gray-800 disabled:opacity-50 font-medium transition-all duration-200"
                          >
                            <span className="sr-only">Previous</span>
                            <ChevronLeft className="h-5 w-5" />
                          </button>
                        </LiquidGlass>
                        
                        {[...Array(totalPages)].map((_, index) => {
                          const pageNumber = index + 1;
                          const isCurrentPage = pageNumber === currentPage;
                          
                          if (
                            pageNumber === 1 ||
                            pageNumber === totalPages ||
                            (pageNumber >= currentPage - 1 && pageNumber <= currentPage + 1)
                          ) {
                            return (
                              <LiquidGlass 
                                key={pageNumber} 
                                variant={isCurrentPage ? "black-button" : "button"} 
                                hover={!isCurrentPage}
                                glowColor={isCurrentPage ? "black" : "white"}
                                bgOpacity={isCurrentPage ? "high" : "medium"}
                              >
                                <button
                                  onClick={() => goToPage(pageNumber)}
                                  className={`relative inline-flex items-center px-4 py-2 text-sm font-bold transition-all duration-200 ${
                                    isCurrentPage
                                      ? 'text-white'
                                      : 'text-gray-600 hover:text-gray-800'
                                  }`}
                                >
                                  {pageNumber}
                                </button>
                              </LiquidGlass>
                            );
                          }
                          
                          if (pageNumber === currentPage - 2 || pageNumber === currentPage + 2) {
                            return (
                              <span
                                key={pageNumber}
                                className="relative inline-flex items-center px-3 py-2 text-sm font-medium text-gray-400"
                              >
                                ...
                              </span>
                            );
                          }
                          
                          return null;
                        })}

                        <LiquidGlass variant="button" hover>
                          <button
                            onClick={() => goToPage(currentPage + 1)}
                            disabled={currentPage === totalPages}
                            className="relative inline-flex items-center px-3 py-2 text-gray-600 hover:text-gray-800 disabled:opacity-50 font-medium transition-all duration-200"
                          >
                            <span className="sr-only">Next</span>
                            <ChevronRight className="h-5 w-5" />
                          </button>
                        </LiquidGlass>
                      </nav>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </LiquidGlass>
        </div>
      )}

      {/* No Events Message */}
      {filteredEvents.length === 0 && !loading && (
        <div className="text-center py-12">
          <Calendar className="w-12 h-12 text-gray-400 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-gray-900 mb-2">No events found</h3>
          <p className="text-gray-600 mb-6">
            {searchTerm ? 'Try adjusting your search terms.' : 'Get started by creating your first event.'}
          </p>
          {!searchTerm && (
            <LiquidGlass variant="black-button" hover glowColor="black" bgOpacity="high">
              <button
                onClick={() => setShowNewEvent(true)}
                className="flex items-center gap-2 px-6 py-3 text-white font-medium mx-auto"
              >
                <Plus className="w-4 h-4" />
                Create Your First Event
              </button>
            </LiquidGlass>
          )}
        </div>
      )}

      {deletingEventId && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50 backdrop-blur-sm">
          <LiquidGlass variant="modal" className="w-full max-w-md p-6" bgOpacity="ultra">
            <h3 className="text-lg font-semibold mb-4 text-gray-800">Delete Event</h3>
            <p className="text-gray-600 mb-6">
              Are you sure you want to delete this event? This action cannot be undone and will also delete all associated results.
            </p>
            <div className="flex justify-end gap-3">
              <LiquidGlass variant="button" hover>
                <button
                  onClick={() => setDeletingEventId(null)}
                  className="px-4 py-2 text-gray-600 hover:text-gray-800"
                >
                  Cancel
                </button>
              </LiquidGlass>
              <LiquidGlass variant="button" hover glowColor="white">
                <button
                  onClick={() => handleDeleteEvent(deletingEventId)}
                  className="px-4 py-2 text-red-600 hover:text-red-700"
                >
                  Delete Event
                </button>
              </LiquidGlass>
            </div>
          </LiquidGlass>
        </div>
      )}

      {showSettings && (
        <EventSettings
          event={events.find(e => e.id === showSettings)!}
          onClose={() => setShowSettings(null)}
          onUpdate={loadEvents}
        />
      )}

      {showShareModal && (
        (() => {
          const event = events.find(e => e.id === showShareModal);
          return event ? (
            <ShareModal
              event={event}
              onClose={() => setShowShareModal(null)}
              onUpdate={loadEvents}
            />
          ) : null;
        })()
      )}
    </div>
  );
}